import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams, Link } from 'react-router-dom';
import { ClaudeMCPCard } from '../components/integrations/ClaudeMCPCard';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Plug, CheckCircle2, XCircle, Loader2, ExternalLink, Copy, RefreshCw,
  Eye, EyeOff, KeyRound, Zap, Link2, AlertTriangle, BookOpen,
  Sparkles, Wallet, Pencil, Check, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Integrations() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/integrations`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Handle OAuth callback redirect (?sellsy=connected / ?sellsy_error=...)
  useEffect(() => {
    const sellsy = params.get('sellsy');
    const sellsyError = params.get('sellsy_error');
    if (sellsy === 'connected') {
      toast.success('Sellsy connecté avec succès');
      params.delete('sellsy');
      setParams(params);
    }
    if (sellsyError) {
      const details = params.get('details') || '';
      toast.error(`Erreur OAuth Sellsy: ${sellsyError}${details ? ' — ' + details : ''}`);
      params.delete('sellsy_error');
      params.delete('details');
      setParams(params);
    }
    const gbp = params.get('gbp');
    const gbpError = params.get('gbp_error');
    if (gbp === 'connected') {
      toast.success('Google Business Profile connecté');
      params.delete('gbp');
      setParams(params);
    }
    if (gbpError) {
      const details = params.get('details') || '';
      toast.error(`Erreur OAuth GBP: ${gbpError}${details ? ' — ' + details : ''}`);
      params.delete('gbp_error');
      params.delete('details');
      setParams(params);
    }
    // Meta Ads OAuth callbacks
    if (params.get('meta') === 'connected') {
      toast.success('Meta Ads connecté avec succès');
      params.delete('meta'); setParams(params);
    }
    const metaError = params.get('meta_error');
    if (metaError) {
      const details = params.get('details') || '';
      toast.error(`Erreur OAuth Meta: ${metaError}${details ? ' — ' + details : ''}`);
      params.delete('meta_error'); params.delete('details'); setParams(params);
    }
    // LinkedIn Ads OAuth callbacks
    if (params.get('linkedin') === 'connected') {
      toast.success('LinkedIn Ads connecté avec succès');
      params.delete('linkedin'); setParams(params);
    }
    const liError = params.get('linkedin_error');
    if (liError) {
      const details = params.get('details') || '';
      toast.error(`Erreur OAuth LinkedIn: ${liError}${details ? ' — ' + details : ''}`);
      params.delete('linkedin_error'); params.delete('details'); setParams(params);
    }
  }, [params, setParams]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="integrations-page">
        {/* ========= SECTION 1: CRM ========= */}
        <section className="space-y-5">
          <header>
            <h1 className="text-3xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <Plug className="w-7 h-7 text-[#002FA7]" />
              Intégrations CRM
            </h1>
            <p className="text-neutral-600 mt-1 text-sm">
              Connectez votre CRM pour marquer automatiquement les leads gagnés/perdus et calculer le ROI réel en closed-loop.
            </p>
          </header>

          {data?.sellsy && <SellsyCard provider={data.sellsy} onChange={reload} />}
          {data?.custom && <CustomCrmCard provider={data.custom} onChange={reload} />}
        </section>

        {/* ========= SECTION 2: GBP ========= */}
        <section className="space-y-5">
          <header>
            <h2 className="text-2xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <span className="text-xl">📍</span> Google Business Profile
            </h2>
            <p className="text-neutral-600 mt-1 text-sm">
              Suivi des impressions, clics, appels et demandes d'itinéraire. Alertes hebdomadaires en cas de baisse significative des appels.
            </p>
          </header>
          <GBPCard onChange={reload} />
        </section>

        {/* ========= SECTION 3: AD PLATFORMS (Meta + LinkedIn) ========= */}
        <section className="space-y-5" data-testid="ad-platforms-section">
          <header>
            <h2 className="text-2xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <span className="text-xl">📣</span> Plateformes publicitaires
            </h2>
            <p className="text-neutral-600 mt-1 text-sm">
              Connectez Meta Ads et LinkedIn Ads pour étendre l'analyse SEA au-delà de Google Ads.
            </p>
          </header>
          <AdPlatformCard
            kind="meta"
            label="Meta Ads"
            subtitle="Facebook + Instagram. OAuth2 avec scopes ads_read / ads_management."
            icon="📘"
            gradient="from-blue-600 to-indigo-600"
            docsUrl="https://developers.facebook.com/docs/marketing-apis/get-started"
            credentialLabels={{ id: 'App ID', secret: 'App Secret' }}
            consoleUrl="https://developers.facebook.com/apps"
            consoleLabel="Meta App Dashboard"
          />
          <AdPlatformCard
            kind="linkedin"
            label="LinkedIn Ads"
            subtitle="LinkedIn Marketing API. ⚠ Nécessite l'approbation de votre app par LinkedIn (2-8 semaines)."
            icon="💼"
            gradient="from-sky-700 to-cyan-600"
            docsUrl="https://learn.microsoft.com/en-us/linkedin/marketing/integrations/marketing-integrations-overview"
            credentialLabels={{ id: 'Client ID', secret: 'Client Secret' }}
            consoleUrl="https://www.linkedin.com/developers/apps"
            consoleLabel="LinkedIn Developer Portal"
            warningText="LinkedIn requiert l'approbation officielle du Marketing Developer Platform pour votre app avant que ces endpoints ne fonctionnent en production."
          />
        </section>

        {/* ========= SECTION 5: CLAUDE MCP ========= */}
        <section className="space-y-5" data-testid="claude-mcp-section">
          <header>
            <h2 className="text-2xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <span className="text-xl">🤝</span> Assistant IA (Claude)
            </h2>
            <p className="text-neutral-600 mt-1 text-sm">
              Donnez à Claude un accès en lecture seule à vos données via le protocole MCP.
            </p>
          </header>
          <ClaudeMCPCard />
        </section>

        {/* ========= SECTION 4: AFFILIATIONS ========= */}
        <AffiliatesSection />
      </div>
    </Layout>
  );
}

// ============= SELLSY CARD =============

function SellsyCard({ provider, onChange }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const connected = provider.has_refresh_token;

  const saveConfig = async () => {
    if (!clientId.trim() && !clientSecret.trim()) {
      toast.error('Renseignez au moins le Client ID');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (clientId.trim()) payload.client_id = clientId.trim();
      if (clientSecret.trim()) payload.client_secret = clientSecret.trim();
      await axios.put(`${API}/integrations/sellsy/config`, payload, { withCredentials: true });
      toast.success('Configuration Sellsy enregistrée');
      setClientId(''); setClientSecret('');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const connectOAuth = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/integrations/sellsy/oauth/start`, { withCredentials: true });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur OAuth');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Sellsy ? Il faudra refaire le flow OAuth.')) return;
    try {
      await axios.delete(`${API}/integrations/sellsy/disconnect`, { withCredentials: true });
      toast.success('Sellsy déconnecté');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.get(`${API}/integrations/sellsy/test-connection`, { withCredentials: true });
      setTestResult(data);
      if (data.success) toast.success('Connexion Sellsy OK');
      else toast.error(data.error || 'Test échoué');
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg" data-testid="integration-sellsy">
      <div className="p-5 border-b border-neutral-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-neutral-900 text-lg flex items-center gap-2">
                Sellsy CRM
                {connected ? (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-green-50 text-green-700 rounded-full font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Connecté
                  </span>
                ) : (
                  <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">Non connecté</span>
                )}
              </h2>
              <p className="text-sm text-neutral-600 mt-0.5">
                CRM français avec OAuth2 + webhooks. Closed-loop automatique des opportunités.
              </p>
              <a
                href={provider.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline mt-1"
              >
                <BookOpen className="w-3 h-3" /> Documentation Sellsy API v2
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Step 1: OAuth credentials */}
        <section>
          <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-neutral-500" />
            1. Identifiants OAuth2
            {provider.has_client_id && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5 mb-2">
            Créez une app OAuth2 dans votre compte Sellsy (type <b>Public</b> ou <b>Private</b>) sur
            <a href="https://www.sellsy.fr/developer/api-v2" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline ml-1">
              sellsy.fr/developer/api-v2
            </a>. Ajoutez cette URL en <b>Redirect URI</b> :
          </p>
          <CopyField value={provider.redirect_uri || ''} testId="sellsy-redirect-uri" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <Input
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              placeholder={provider.has_client_id ? 'Client ID (•••• — conservé)' : 'Client ID'}
              data-testid="sellsy-client-id-input"
            />
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                placeholder={provider.has_client_secret ? 'Client Secret (•••• — conservé)' : 'Client Secret'}
                className="pr-10"
                data-testid="sellsy-client-secret-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400"
                aria-label="Afficher"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button
              onClick={saveConfig}
              disabled={saving || (!clientId.trim() && !clientSecret.trim())}
              size="sm"
              className="bg-[#002FA7] hover:bg-[#002277]"
              data-testid="sellsy-save-config"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </section>

        {/* Step 2: OAuth connection */}
        <section className="border-t border-neutral-100 pt-4">
          <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-neutral-500" />
            2. Connexion OAuth2
            {connected && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5 mb-2">
            Autorisez l'application à accéder aux opportunités et contacts de votre compte Sellsy.
          </p>
          <div className="flex flex-wrap gap-2">
            {!connected ? (
              <Button
                onClick={connectOAuth}
                disabled={connecting || !provider.has_client_id}
                className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white"
                data-testid="sellsy-connect-oauth"
              >
                {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Connecter Sellsy
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="sellsy-test-connection">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Tester la connexion
                </Button>
                <Button variant="outline" onClick={disconnect} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="sellsy-disconnect">
                  Déconnecter
                </Button>
              </>
            )}
          </div>
          {testResult && (
            <div className={`mt-2 flex items-start gap-2 text-xs p-2 rounded ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {testResult.success ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
              <span>{testResult.success ? `Connexion OK (${testResult.total_staffs ?? '?'} collaborateurs détectés)` : (testResult.error || 'Échec')}</span>
            </div>
          )}
        </section>

        {/* Step 3: Webhook */}
        <section className="border-t border-neutral-100 pt-4">
          <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <Plug className="w-4 h-4 text-neutral-500" />
            3. Webhook Sellsy
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5 mb-2">
            Dans Sellsy → <b>Paramètres → API → Webhooks</b>, créez un webhook HTTP avec cette URL et activez les événements <code className="bg-neutral-100 px-1 rounded">opportunity.*</code> :
          </p>
          <CopyField value={provider.webhook_url || ''} testId="sellsy-webhook-url" />
          <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Le token dans l'URL sert de secret partagé. Ne le publiez pas. Les leads sont matchés par
              <b> email du contact</b> (comparé à <code>from_email</code> des leads Gmail).
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}

// ============= CUSTOM CRM CARD =============

function CustomCrmCard({ provider, onChange }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCfg = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/custom/config`, { withCredentials: true });
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  }, []);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/custom/config`, {
        name: cfg.name,
        email_path: cfg.email_path,
        status_path: cfg.status_path,
        value_path: cfg.value_path,
        external_ref_path: cfg.external_ref_path,
        won_values: cfg.won_values,
        lost_values: cfg.lost_values,
        qualified_values: cfg.qualified_values,
      }, { withCredentials: true });
      toast.success('Configuration enregistrée');
      loadCfg();
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const regenerateToken = async () => {
    if (!window.confirm('Régénérer le token ? L\'ancienne URL de webhook sera invalidée.')) return;
    try {
      const { data } = await axios.post(`${API}/integrations/custom/regenerate-token`, {}, { withCredentials: true });
      setCfg(c => ({ ...c, webhook_url: data.webhook_url }));
      toast.success('Nouveau token généré');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (!cfg) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg" data-testid="integration-custom">
      <div className="p-5 border-b border-neutral-100">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Plug className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-semibold text-neutral-900 text-lg flex items-center gap-2">
              CRM personnalisé / Zapier / Make
              <span className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium">
                Générique
              </span>
            </h2>
            <p className="text-sm text-neutral-600 mt-0.5">
              Endpoint webhook universel. Connectez n'importe quel CRM via Zapier, Make, ou un appel direct.
              Les champs sont extraits par <b>JSONPath</b>.
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Webhook URL */}
        <section>
          <h3 className="text-sm font-semibold text-neutral-900 mb-2">URL de webhook</h3>
          <CopyField value={cfg.webhook_url || ''} testId="custom-webhook-url" />
          <Button size="sm" variant="outline" onClick={regenerateToken} className="mt-2" data-testid="custom-regenerate-token">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Régénérer le token
          </Button>
        </section>

        {/* JSONPath mapping */}
        <section className="border-t border-neutral-100 pt-4">
          <h3 className="text-sm font-semibold text-neutral-900">Mapping des champs (JSONPath)</h3>
          <p className="text-xs text-neutral-500 mt-0.5 mb-3">
            Exemple : si votre payload est <code className="bg-neutral-100 px-1 rounded">{`{"deal":{"contact":{"email":"x@y.com"},"stage":"won","amount":2500}}`}</code>, utilisez
            <code className="bg-neutral-100 px-1 rounded ml-1">deal.contact.email</code>,
            <code className="bg-neutral-100 px-1 rounded ml-1">deal.stage</code>,
            <code className="bg-neutral-100 px-1 rounded ml-1">deal.amount</code>. Les index de liste sont supportés (ex. <code>items.0.name</code>).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nom du CRM (affichage)">
              <Input value={cfg.name || ''} onChange={e => setCfg(c => ({ ...c, name: e.target.value }))}
                placeholder="Ex. Pipedrive, HubSpot..." data-testid="custom-name" />
            </Field>
            <Field label="Chemin vers l'email du contact (requis)">
              <Input value={cfg.email_path || ''} onChange={e => setCfg(c => ({ ...c, email_path: e.target.value }))}
                placeholder="deal.contact.email" data-testid="custom-email-path" />
            </Field>
            <Field label="Chemin vers le statut (optionnel)">
              <Input value={cfg.status_path || ''} onChange={e => setCfg(c => ({ ...c, status_path: e.target.value }))}
                placeholder="deal.stage" data-testid="custom-status-path" />
            </Field>
            <Field label="Chemin vers la valeur (€)">
              <Input value={cfg.value_path || ''} onChange={e => setCfg(c => ({ ...c, value_path: e.target.value }))}
                placeholder="deal.amount" data-testid="custom-value-path" />
            </Field>
            <Field label="Chemin vers la référence externe">
              <Input value={cfg.external_ref_path || ''} onChange={e => setCfg(c => ({ ...c, external_ref_path: e.target.value }))}
                placeholder="deal.id" data-testid="custom-ref-path" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <Field label="Valeurs → WON (CSV)">
              <Input value={cfg.won_values || ''} onChange={e => setCfg(c => ({ ...c, won_values: e.target.value }))} data-testid="custom-won-values" />
            </Field>
            <Field label="Valeurs → LOST (CSV)">
              <Input value={cfg.lost_values || ''} onChange={e => setCfg(c => ({ ...c, lost_values: e.target.value }))} data-testid="custom-lost-values" />
            </Field>
            <Field label="Valeurs → QUALIFIED (CSV)">
              <Input value={cfg.qualified_values || ''} onChange={e => setCfg(c => ({ ...c, qualified_values: e.target.value }))} data-testid="custom-qualified-values" />
            </Field>
          </div>

          <div className="flex justify-end mt-3">
            <Button onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="custom-save-config">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer le mapping'}
            </Button>
          </div>
        </section>

        <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded p-2">
          Si le mapping n'est pas configuré, l'endpoint accepte les payloads mais les logue sans traitement
          (utile pour inspecter la structure depuis la page <b>Logs</b>).
        </div>
      </div>
    </div>
  );
}

// ============= SHARED UI =============

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-neutral-600 uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function CopyField({ value, testId }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copié');
    } catch {
      toast.error('Copie impossible');
    }
  };
  return (
    <div className="flex gap-2">
      <Input value={value} readOnly className="font-mono text-xs" data-testid={testId} />
      <Button size="sm" variant="outline" onClick={copy} data-testid={`${testId}-copy`}>
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  );
}



// ============= GBP CARD =============

function GBPCard({ onChange }) {
  const [cfg, setCfg] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/google-business/config`, { withCredentials: true });
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;

  const connected = cfg.has_refresh_token;

  const saveConfig = async () => {
    if (!clientId.trim() && !clientSecret.trim()) {
      toast.error('Renseignez au moins le Client ID');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (clientId.trim()) payload.client_id = clientId.trim();
      if (clientSecret.trim()) payload.client_secret = clientSecret.trim();
      await axios.put(`${API}/google-business/config`, payload, { withCredentials: true });
      toast.success('Configuration GBP enregistrée');
      setClientId(''); setClientSecret('');
      load(); if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const clearConfig = async () => {
    if (!window.confirm('Effacer les identifiants GBP enregistrés (Client ID + Secret + connexion) ?')) return;
    try {
      await axios.delete(`${API}/google-business/config`, { withCredentials: true });
      toast.success('Identifiants GBP effacés — vous pouvez repartir de zéro');
      setClientId(''); setClientSecret('');
      load(); if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const connectOAuth = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${API}/google-business/oauth/start`, { withCredentials: true });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur OAuth');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Google Business Profile ?')) return;
    try {
      await axios.delete(`${API}/google-business/disconnect`, { withCredentials: true });
      toast.success('GBP déconnecté');
      load(); if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.get(`${API}/google-business/test-connection`, { withCredentials: true });
      setTestResult(data);
      if (data.success) toast.success('Connexion GBP OK');
      else toast.error(data.error || 'Test échoué');
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg" data-testid="integration-gbp">
      <div className="p-5 border-b border-neutral-100">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 text-2xl">
            📍
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-neutral-900 text-lg flex items-center gap-2">
              Google Business Profile
              {connected ? (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-green-50 text-green-700 rounded-full font-medium">
                  <CheckCircle2 className="w-3 h-3" /> Connecté
                </span>
              ) : (
                <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">Non connecté</span>
              )}
            </h3>
            <p className="text-sm text-neutral-600 mt-0.5">
              Suivi impressions, clics, appels, itinéraires. OAuth2 Google avec scope <code className="bg-neutral-100 px-1 rounded text-xs">business.manage</code>.
            </p>
            <a
              href="https://developers.google.com/my-business/content/basic-setup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline mt-1"
            >
              <BookOpen className="w-3 h-3" /> Documentation + prérequis API
            </a>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <section>
          <h4 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-neutral-500" /> 1. Identifiants OAuth2
            {cfg.has_client_id && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </h4>
          <p className="text-xs text-neutral-500 mt-0.5 mb-2">
            Créez une app OAuth2 dans <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">Google Cloud Console</a>, activez les APIs <b>My Business Account Management, Business Information, Business Profile Performance</b>, puis ajoutez cette URL en Redirect URI :
          </p>
          <CopyField value={cfg.redirect_uri || ''} testId="gbp-redirect-uri" />
          {cfg.has_client_id && (
            cfg.client_id_looks_valid ? (
              <div className="mt-2 space-y-2" data-testid="gbp-client-id-preview">
                <p className="text-[11px] text-neutral-500">Client ID enregistré dans l'app :</p>
                <CopyField value={cfg.client_id_full || cfg.client_id_preview} testId="gbp-client-id-full" />
                <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2" data-testid="gbp-mismatch-checklist">
                  <b>En cas d'erreur « redirect_uri_mismatch »</b> : dans Google Cloud Console → Identifiants,
                  ouvrez le client OAuth dont l'ID correspond <b>exactement</b> à celui ci-dessus
                  (attention si vous avez plusieurs projets : Odoo, ProjetMAnagementSEO…) et vérifiez que
                  l'« URI de redirection autorisé » ci-dessus y figure au caractère près (https, pas de / final).
                  Patientez 2-5 min après l'enregistrement.
                </div>
              </div>
            ) : (
              <div className="mt-2 text-xs bg-red-50 border border-red-200 text-red-800 rounded p-2" data-testid="gbp-client-id-invalid">
                ⚠ Le Client ID enregistré (<code>{cfg.client_id_preview}</code>) est <b>invalide</b> — c'est la cause de l'erreur Google « invalid_client ».
                Format attendu : <code>123456789012-xxxxxxxx.apps.googleusercontent.com</code>.
                Recopiez-le depuis Google Cloud Console → Identifiants → votre client OAuth 2.0, puis ré-enregistrez.
              </div>
            )
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <Input
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              placeholder={cfg.has_client_id ? 'Client ID (•••• — conservé)' : 'Client ID'}
              data-testid="gbp-client-id-input"
            />
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                placeholder={cfg.has_client_secret ? 'Client Secret (•••• — conservé)' : 'Client Secret'}
                className="pr-10"
                data-testid="gbp-client-secret-input"
              />
              <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400">
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            {cfg.has_client_id && (
              <Button onClick={clearConfig} size="sm" variant="outline"
                className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="gbp-clear-config">
                Effacer les identifiants
              </Button>
            )}
            <Button onClick={saveConfig} disabled={saving || (!clientId.trim() && !clientSecret.trim())}
              size="sm" className="bg-[#002FA7] hover:bg-[#002277]" data-testid="gbp-save-config">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </section>

        <section className="border-t border-neutral-100 pt-4">
          <h4 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-neutral-500" /> 2. Connexion OAuth2
            {connected && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </h4>
          <p className="text-xs text-neutral-500 mt-0.5 mb-2">
            Autorisez l'accès aux fiches GBP. ⚠ L'accès API n'est accordé qu'après validation manuelle par Google (quelques jours — faites la demande via <a href="https://support.google.com/business/contact/api_default" target="_blank" rel="noopener noreferrer" className="underline">ce formulaire</a>).
          </p>
          <div className="flex flex-wrap gap-2">
            {!connected ? (
              <Button onClick={connectOAuth} disabled={connecting || !cfg.has_client_id}
                className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white hover:opacity-90"
                data-testid="gbp-connect-oauth">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Connecter Google Business Profile
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="gbp-test-connection">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Tester ({testResult?.accounts_count ?? '?'} comptes)
                </Button>
                <Button variant="outline" onClick={disconnect} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="gbp-disconnect">
                  Déconnecter
                </Button>
              </>
            )}
          </div>
          {testResult && (
            <div className={`mt-2 flex items-start gap-2 text-xs p-2 rounded ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {testResult.success ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
              <span>{testResult.success ? `${testResult.accounts_count ?? 0} comptes GBP détectés` : (testResult.error || 'Échec')}</span>
            </div>
          )}
        </section>

        {connected && (
          <div className="border-t border-neutral-100 pt-4 bg-blue-50 -mx-5 -mb-5 px-5 py-3 rounded-b-lg">
            <p className="text-xs text-blue-900">
              🔗 <b>Étape suivante</b> : rendez-vous sur la page <Link to="/clients" className="underline font-medium">Clients</Link> pour lier chaque client à sa fiche Google Business Profile.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}



// ============= AFFILIATES SECTION =============

function AffiliatesSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/affiliates`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) {
    return (
      <section className="py-12 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7] mx-auto" />
      </section>
    );
  }

  const filtered = activeCategory === 'all'
    ? data.programs
    : data.programs.filter(p => p.category === activeCategory);

  return (
    <section className="space-y-5" data-testid="affiliates-section">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-heading font-bold text-neutral-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-[#002FA7]" />
            Connecteurs utiles
          </h2>
          <p className="text-neutral-600 mt-1 text-sm max-w-2xl">
            Mes liens d'affiliation personnels pour chaque outil utilisé dans la plateforme.
            Quand un utilisateur souscrit via ces liens, mon entreprise génère une commission.
          </p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white px-4 py-2.5 rounded-lg shadow-sm">
          <div className="text-[11px] opacity-90 uppercase tracking-wide font-medium">Configurés</div>
          <div className="text-xl font-bold" data-testid="affiliates-configured-count">
            {data.total_configured}/{data.total_catalog}
          </div>
        </div>
      </header>

      <div className="flex gap-2 flex-wrap border-b border-neutral-200 pb-2">
        <CategoryTab label="Tous" active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} count={data.programs.length} />
        {data.categories.map(c => (
          <CategoryTab
            key={c.id}
            label={c.label}
            active={activeCategory === c.id}
            onClick={() => setActiveCategory(c.id)}
            count={data.programs.filter(p => p.category === c.id).length}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(program => (
          <AffiliateCard key={program.id} program={program} onChange={reload} />
        ))}
      </div>
    </section>
  );
}

function CategoryTab({ label, active, onClick, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
        active ? 'bg-[#002FA7] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
      }`}
      data-testid={`affiliate-tab-${label.toLowerCase().replace(/ /g, '-')}`}
    >
      {label} <span className="opacity-75">· {count}</span>
    </button>
  );
}

function AffiliateCard({ program, onChange }) {
  const [editing, setEditing] = useState(false);
  const [affiliateUrl, setAffiliateUrl] = useState(program.affiliate_url || '');
  const [commissionNote, setCommissionNote] = useState(program.commission_note || '');
  const [enabled, setEnabled] = useState(program.enabled !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/integrations/affiliates/${program.id}`,
        {
          affiliate_url: affiliateUrl.trim(),
          commission_note: commissionNote.trim(),
          enabled,
        },
        { withCredentials: true }
      );
      toast.success(`${program.name} mis à jour`);
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setAffiliateUrl(program.affiliate_url || '');
    setCommissionNote(program.commission_note || '');
    setEnabled(program.enabled !== false);
    setEditing(false);
  };

  const configured = !!program.affiliate_url;
  const displayUrl = program.affiliate_url || program.default_url;

  return (
    <div
      className={`bg-white border rounded-lg p-4 transition-all ${
        configured ? 'border-emerald-200 bg-emerald-50/30' : 'border-neutral-200'
      }`}
      data-testid={`affiliate-card-${program.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0 text-xl">
          {program.logo_emoji || '🔗'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-neutral-900 truncate">{program.name}</h3>
            <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded-full font-medium uppercase">
              {program.category_label}
            </span>
            {configured ? (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium uppercase">
                <Sparkles className="w-2.5 h-2.5" /> Mon lien
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium uppercase">
                À configurer
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-600 mt-0.5">{program.description}</p>

          {!editing && (
            <div className="mt-2 space-y-1">
              {program.commission_note && (
                <div className="text-[11px] text-emerald-700 font-medium">
                  💰 {program.commission_note}
                </div>
              )}
              <div className="flex items-center gap-2">
                <a
                  href={displayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline truncate max-w-[220px]"
                  data-testid={`affiliate-link-${program.id}`}
                >
                  {configured ? 'Tester mon lien' : 'Voir l\'outil'} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(true)}
                  className="h-7 text-[11px] px-2"
                  data-testid={`affiliate-edit-${program.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" /> {configured ? 'Modifier' : 'Ajouter mon lien'}
                </Button>
              </div>
            </div>
          )}

          {editing && (
            <div className="mt-2 space-y-2">
              <Input
                value={affiliateUrl}
                onChange={e => setAffiliateUrl(e.target.value)}
                placeholder={`https://${program.id}.com/?ref=votre_id`}
                className="text-xs"
                data-testid={`affiliate-url-input-${program.id}`}
              />
              <Input
                value={commissionNote}
                onChange={e => setCommissionNote(e.target.value)}
                placeholder="Commission / note (ex. 20% récurrent)"
                className="text-xs"
                data-testid={`affiliate-note-input-${program.id}`}
              />
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-xs text-neutral-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={e => setEnabled(e.target.checked)}
                    className="rounded"
                    data-testid={`affiliate-enabled-${program.id}`}
                  />
                  Actif
                </label>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={cancel} className="h-7 text-[11px]">
                    <X className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving}
                    className="bg-[#002FA7] hover:bg-[#002277] h-7 text-[11px]"
                    data-testid={`affiliate-save-${program.id}`}
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Sauver</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ============= AD PLATFORM CARD (Meta + LinkedIn shared) =============

function AdPlatformCard({
  kind, label, subtitle, icon, gradient,
  docsUrl, credentialLabels, consoleUrl, consoleLabel, warningText,
}) {
  const [cfg, setCfg] = useState(null);
  const [idVal, setIdVal] = useState('');
  const [secretVal, setSecretVal] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const apiBase = `${API}/${kind === 'meta' ? 'meta-ads' : 'linkedin-ads'}`;
  const idField = kind === 'meta' ? 'app_id' : 'client_id';
  const secretField = kind === 'meta' ? 'app_secret' : 'client_secret';
  const hasIdKey = kind === 'meta' ? 'has_app_id' : 'has_client_id';
  const hasSecretKey = kind === 'meta' ? 'has_app_secret' : 'has_client_secret';

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/config`, { withCredentials: true });
      setCfg(data);
    } catch (e) {
      // silent — admin may have an outage
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;

  const connected = cfg.is_connected;

  const saveConfig = async () => {
    if (!idVal.trim() && !secretVal.trim()) {
      toast.error(`Renseignez au moins le ${credentialLabels.id}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (idVal.trim()) payload[idField] = idVal.trim();
      if (secretVal.trim()) payload[secretField] = secretVal.trim();
      await axios.put(`${apiBase}/config`, payload, { withCredentials: true });
      toast.success(`${label} : configuration enregistrée`);
      setIdVal(''); setSecretVal('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const connectOAuth = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`${apiBase}/oauth/start`, { withCredentials: true });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur OAuth');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Déconnecter ${label} ?`)) return;
    try {
      await axios.delete(`${apiBase}/disconnect`, { withCredentials: true });
      toast.success(`${label} déconnecté`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.get(`${apiBase}/test-connection`, { withCredentials: true });
      setTestResult(data);
      if (data.success) toast.success(`${label} : connexion OK`);
      else toast.error(data.error || 'Test échoué');
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg" data-testid={`integration-${kind}`}>
      <div className="p-5 border-b border-neutral-100">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center flex-shrink-0 text-2xl text-white`}>
            {icon}
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-neutral-900 text-lg flex items-center gap-2">
              {label}
              {connected ? (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-green-50 text-green-700 rounded-full font-medium">
                  <CheckCircle2 className="w-3 h-3" /> Connecté
                </span>
              ) : (
                <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">Non connecté</span>
              )}
            </h3>
            <p className="text-sm text-neutral-600 mt-0.5">{subtitle}</p>
            <a href={docsUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline mt-1">
              <BookOpen className="w-3 h-3" /> Documentation officielle
            </a>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {warningText && (
          <div className="flex items-start gap-2 text-xs p-2.5 rounded bg-amber-50 border border-amber-200 text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{warningText}</span>
          </div>
        )}

        <section>
          <h4 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-neutral-500" /> 1. Identifiants OAuth2
            {cfg[hasIdKey] && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </h4>
          <p className="text-xs text-neutral-500 mt-0.5 mb-2">
            Créez une app sur <a href={consoleUrl} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">{consoleLabel}</a>, puis ajoutez cette URL en Redirect URI :
          </p>
          <CopyField value={cfg.redirect_uri || ''} testId={`${kind}-redirect-uri`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <Input
              value={idVal}
              onChange={e => setIdVal(e.target.value)}
              placeholder={cfg[hasIdKey] ? `${credentialLabels.id} (•••• — conservé)` : credentialLabels.id}
              data-testid={`${kind}-id-input`}
            />
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={secretVal}
                onChange={e => setSecretVal(e.target.value)}
                placeholder={cfg[hasSecretKey] ? `${credentialLabels.secret} (•••• — conservé)` : credentialLabels.secret}
                className="pr-10"
                data-testid={`${kind}-secret-input`}
              />
              <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400">
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button onClick={saveConfig} disabled={saving || (!idVal.trim() && !secretVal.trim())}
              size="sm" className="bg-[#002FA7] hover:bg-[#002277]" data-testid={`${kind}-save-config`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </section>

        <section className="border-t border-neutral-100 pt-4">
          <h4 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-neutral-500" /> 2. Connexion OAuth2
            {connected && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          </h4>
          <div className="flex flex-wrap gap-2 mt-2">
            {!connected ? (
              <Button onClick={connectOAuth} disabled={connecting || !cfg[hasIdKey]}
                className={`bg-gradient-to-r ${gradient} text-white hover:opacity-90`}
                data-testid={`${kind}-connect-oauth`}>
                {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Connecter {label}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={testConnection} disabled={testing} data-testid={`${kind}-test-connection`}>
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Tester la connexion
                </Button>
                <Button variant="outline" onClick={disconnect} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`${kind}-disconnect`}>
                  Déconnecter
                </Button>
              </>
            )}
          </div>
          {testResult && (
            <div className={`mt-2 flex items-start gap-2 text-xs p-2 rounded ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {testResult.success ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
              <span>{testResult.success ? 'Connexion validée' : (testResult.error || 'Échec')}</span>
            </div>
          )}
        </section>

        {connected && (
          <div className="border-t border-neutral-100 pt-4 bg-blue-50 -mx-5 -mb-5 px-5 py-3 rounded-b-lg">
            <p className="text-xs text-blue-900">
              🔗 <b>Étape suivante</b> : utilisez l'API <code className="bg-white px-1 rounded">{apiBase}/accounts</code> pour lister vos comptes publicitaires, puis liez-les à vos clients via <code className="bg-white px-1 rounded">PUT {apiBase}/clients/{'{id}'}/link</code>. La page Clients exposera une UI dédiée prochainement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
